import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { Observable, Subscription, combineLatest, isObservable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MatSidenav } from '@angular/material/sidenav'; // new

import { IconNameMap, FcpIconSVGColors } from 'fc-ui-components';

import { findDataInSnapshot, getItemAsJson } from 'helpers';
import { AuthenticatedUserService, SegmentService, UserService } from 'services';
import { ContentService } from 'app/content/retailer-platform/content/content.service';
import { TrackingEvents } from 'shared/services/tracking/gtm';
import { MainNavigationService } from './main-navigation.service';
import { PlatformNavigationMenuItem, PlatformToolbarAction, PlatformToolbarLink } from './types';
import { ShoppingCartService } from 'app/b2b-order/services/shopping-cart';
import { OrderSheetService } from 'app/b2b-order/services/order-sheet';
import { DownloadsService } from 'app/content/services/downloads.service';
import { RetailerMainNavigationItem } from 'app/retailer-platform/shared/types';
import { ManufacturerMainNavigationItem } from 'app/manufacturer-platform/types';

@Component({
    selector: 'fc-main-navigation',
    templateUrl: './main-navigation.component.html',
    styleUrls: ['./main-navigation.component.less'],
    standalone: false
})
export class MainNavigationComponent implements OnInit, AfterViewInit {
    @Input() menuList: PlatformNavigationMenuItem[];
    @Output() settingsButtonClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() cartButtonClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() brandSearchButtonClicked: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('cart') public cartDrawer: MatSidenav;

    public contentViewMode = Boolean(_.get(getItemAsJson('appSettings'), 'contentViewMode', false));
    public settingsListForMenu;
    public toolbarAction: Observable<PlatformToolbarAction & { counterFn: () => number }>;
    public toolbarStoresAction: Observable<PlatformToolbarAction>;
    public activeMenuTitle: Observable<string>;
    public isRetailer: boolean;
    public displayStoreDropdownToggle = false;
    public storeCount: number;
    public authenticatedUserSubscriber: Subscription;
    public shoppingCartCount: Observable<number>;
    public downloadsCount: Observable<number>;
    public isSettingsOpen = false;
    public isToolbarVisibleForDesktop: Observable<boolean>;
    public toolbarLinks: Observable<PlatformToolbarLink[]>;

    iconNameMap = IconNameMap;
    fcpIconSVGColors = FcpIconSVGColors;
    cartDrawerSubscription: Subscription;

    constructor(
        private authenticatedUserService: AuthenticatedUserService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private segmentService: SegmentService,
        private mainNavigationService: MainNavigationService,
        private userService: UserService,
        private shoppingCartService: ShoppingCartService,
        private orderSheetService: OrderSheetService,
        private downloadsService: DownloadsService,
        private contentService: ContentService
    ) {}

    ngOnInit(): void {
        this.activeMenuTitle = this.mainNavigationService.activePageTitle;

        this.isToolbarVisibleForDesktop = this.mainNavigationService.isToolbarVisibleForDesktop;
        this.toolbarLinks = this.mainNavigationService.toolbarLinks;

        this.toolbarAction = this.mainNavigationService.toolbarAction.pipe(
            map((toolbarAction) =>
                toolbarAction
                    ? {
                          ...toolbarAction,
                          counter: isObservable(toolbarAction.counter)
                              ? toolbarAction.counter
                              : typeof toolbarAction.counter === 'number'
                                ? of(toolbarAction.counter)
                                : null,
                          counterFn: typeof toolbarAction.counter === 'function' ? toolbarAction.counter : null
                      }
                    : null
            )
        );

        this.toolbarStoresAction = this.mainNavigationService.toolbarStoresAction.pipe(
            map((toolbarAction) =>
                toolbarAction
                    ? {
                          ...toolbarAction,
                          counter: this.storeCount
                      }
                    : null
            )
        );

        this.isRetailer = this.userService.isRetailer();

        this.authenticatedUserSubscriber = this.authenticatedUserService.isLoggedIn.subscribe((isLoggedIn) => {
            if (!isLoggedIn) {
                this.shoppingCartService.cartSanitization();
            }
        });

        this.shoppingCartCount = this.shoppingCartCount = combineLatest([
            this.shoppingCartService.count,
            this.orderSheetService.count
        ]).pipe(map(([shoppingCartCount, orderSheetCount]) => shoppingCartCount || orderSheetCount));

        this.downloadsCount = this.downloadsService.getDownloadListUpdated().pipe(
            map(() => this.downloadsService.getCount()),
            shareReplay()
        );

        this.settingsListForMenu = this.mainNavigationService.settingsList.pipe(
            map((settingsList) =>
                settingsList.map((settingsItem) => {
                    if ('text' in settingsItem) {
                        return settingsItem;
                    }

                    return {
                        text: settingsItem.label,
                        value: settingsItem.route.target
                    };
                })
            )
        );
    }

    ngAfterViewInit() {
        this.cartDrawerSubscription = this.contentService.getDownloadListToggle().subscribe(() => {
            this.toggleDownloadList();
        });

        this.activatedRoute.queryParams.subscribe((params) => {
            if (params.multiDownloadDrawer === 'true') {
                this.toggleDownloadList();
            }
        });
    }

    public isRouteActive(route: string) {
        return this.router.url.endsWith(route);
    }

    openSettings() {
        this.settingsButtonClicked.emit();
    }

    onSettingsMenuOpened() {
        this.mainNavigationService.sendNavigationMenuItemTrackEvent({
            eventName: '[PXP] Primary Navigation item clicked',
            eventPayload: {
                target: 'Settings'
            }
        });
        this.isSettingsOpen = true;
        this.onNavigationClick();
    }

    onSettingsMenuClosed() {
        this.isSettingsOpen = false;
    }

    onDownloadListClick(event: MouseEvent) {
        this.mainNavigationService.sendNavigationMenuItemTrackEvent({
            eventName: '[PXP] Primary Navigation item clicked',
            eventPayload: {
                target: 'Downloads Cart'
            }
        });
        event.stopPropagation();
        this.toggleDownloadList();
    }

    toggleDownloadList() {
        this.cartDrawer.toggle();
        this.mainNavigationService.setIsDownloadListOpen(this.cartDrawer.opened);
    }

    onNavigationClick() {
        if (this.cartDrawer.opened) {
            this.cartDrawer.close();
            this.mainNavigationService.setIsDownloadListOpen(this.cartDrawer.opened);
        }
    }

    openShoppingCart() {
        this.mainNavigationService.sendNavigationMenuItemTrackEvent({
            eventName: '[PXP] Primary Navigation item clicked',
            eventPayload: {
                target: 'Shopping Cart'
            }
        });
        this.cartButtonClicked.emit();
    }

    onBrandSearchButtonClick() {
        this.segmentService.track('[PX] Brand Search Opened');
        this.brandSearchButtonClicked.emit();
    }

    onMenuItemTriggered({ value }) {
        if (value) {
            this.router.navigate([value]);
        }
    }

    public displayStoresDropdown() {
        this.displayStoreDropdownToggle = !this.displayStoreDropdownToggle;
        this.segmentService.track(TrackingEvents.EDIT_STORES_DROPDOWN_CLICK, {
            page_type: findDataInSnapshot(this.activatedRoute.snapshot, 'selectedMenu')
        });
    }

    public setStoreCounter(count: number) {
        this.storeCount = count;
    }

    onMenuItemClick(item: PlatformNavigationMenuItem) {
        const target = Array.isArray(item.route.target) ? item.route.target.join('/') : item.route.target;

        const navItems = [
            ...Object.values(RetailerMainNavigationItem),
            ...Object.values(ManufacturerMainNavigationItem)
        ];

        const matchedNavItem = navItems.find((key) => {
            return target.split('/').some((segment) => segment === key);
        });

        this.mainNavigationService.sendNavigationMenuItemTrackEvent({
            eventName: '[PXP] Primary Navigation item clicked',
            eventPayload: {
                target: _.startCase(matchedNavItem || target)
            }
        });
    }

    onToolbarItemClick(item: PlatformToolbarLink) {
        if (!item?.id) {
            return;
        }

        this.mainNavigationService.sendNavigationMenuItemTrackEvent({
            eventName: '[PXP] Secondary Navigation item clicked',
            eventPayload: {
                target: item?.id
            }
        });
    }

    onReturnToAdminClick() {
        this.authenticatedUserService.stopImpersonatingUser();
    }

    onLogoClick() {
        this.mainNavigationService.sendNavigationMenuItemTrackEvent({
            eventName: '[PXP] Primary Navigation item clicked',
            eventPayload: {
                target: 'FC Logo'
            }
        });
    }
}
